<template>
  <div :class="{ 'cfs-pdf': !showLayout }">
    <slot name="activator" :download="download" />
    <vue-html2pdf
      ref="pdfRef"
      :show-layout="showLayout"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="previewModal"
      :manual-pagination="true"
      :html-to-pdf-options="{
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        margin: pageMargin?.length ? pageMargin : autoHeader ? [40, 0, 15, 0] : [5, 0, 15, 0],
        filename: `${title}.pdf`,
        jsPDF: {
          format: 'a3',
          orientation: orientation,
        },
      }"
      :pdf-quality="2"
      :pdf-orientation="orientation"
      pdf-content-width="100%"
      @beforeDownload="beforeDownload($event)"
    >
      <template #pdf-content>
        <v-app>
          <v-container fluid class="grow-0 px-8 flex flex-col">
            <template v-if="$hasSlot('custom-template')">
              <slot name="custom-template" />
            </template>
            <template v-else>
              <HeaderPDF
                v-if="!autoHeader"
                :hazardous="hazardous"
                :headload="headload"
                :title="title"
                :sub-title="subTitle"
              />

              <slot />
            </template>
          </v-container>
        </v-app>
      </template>
    </vue-html2pdf>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import HeaderPDF from '@/components/pdfs/HeaderPDF.vue';
import { useUserStore } from '@/store/modules/user/useUserStore';
import { storeToRefs } from 'pinia';
import date, { DateFormat } from '@/extensions/filters/date';
import { useOrganizationStore } from '@/store/modules/useOrganizationStore';
import { barCode, logoImg } from '@/constants/pdf-images';

export default defineComponent({
  name: 'CfsPdf',
  components: {
    HeaderPDF,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      default: '',
    },
    previewModal: {
      type: Boolean,
      default: false,
    },
    pageMargin: {
      type: Array,
      default: null,
    },
    preDownload: {
      type: Function as PropType<
        (pdf: any, pageInfo?: { currentPage: number; totalPages: number }) => void
      >,
      default: () => null,
    },
    showLayout: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String as PropType<'portrait' | 'landscape'>,
      default: 'portrait',
    },
    autoHeader: {
      type: Boolean,
      default: false,
    },
    titleX: {
      type: Number,
      default: null,
    },
    subtitleX: {
      type: Number,
      default: null,
    },
    hazardous: {
      type: Boolean,
      default: false,
    },
    headload: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['downloaded'],
  setup(props, { emit }) {
    const pdfRef = ref();
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const warehouse = computed(() => organizationStore.currentWarehouse);
    const { currentUserName } = storeToRefs(userStore);

    const beforeDownload = async ({ html2pdf, options, pdfContent }: any) => {
      await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let currentPage = 1; currentPage <= totalPages; currentPage++) {
            pdf.setPage(currentPage);

            if (props.autoHeader) {
              pdf.addImage(barCode, 'PNG', 250, 17, 38, 7);
              pdf.addImage(logoImg, 'PNG', 8, 8, 25, 11);
              pdf.setTextColor('#000');
              pdf.setFont('helvetica', 'normal', 'bold');
              pdf.setFontSize(14);
              pdf.setFontType('bold');
              pdf.text(warehouse.value?.name, 35, 14);
              pdf.text(props.subTitle, props.subtitleX || 250, 29);
              pdf.setTextColor(150);
              pdf.text(props.title, props.titleX || 250, 15);
              pdf.setTextColor('#000');
              pdf.setFont('helvetica', 'normal', 'normal');
              pdf.setFontSize(12);
              pdf.text(warehouse.value?.address?.line1, 35, 20);
              pdf.text(
                `${warehouse.value?.address?.city}, ${warehouse.value?.address?.state?.name} ${warehouse.value?.address?.zipCode}`,
                35,
                26
              );
              pdf.text(`Phone: ${warehouse.value?.contacts[0]?.phoneNumber}`, 35, 32);
              pdf.text(`www.coda-usa.com`, 35, 38);
            }

            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
              `Page ${currentPage} of ${totalPages}`,
              pdf.internal.pageSize.getWidth() * 0.91,
              pdf.internal.pageSize.getHeight() - 8
            );
            pdf.text(
              `Printed on ${date(new Date(), DateFormat.FullDate)}`,
              pdf.internal.pageSize.getWidth() * 0.03,
              pdf.internal.pageSize.getHeight() - 13
            );
            pdf.text(
              `Printed by ${currentUserName.value}`,
              pdf.internal.pageSize.getWidth() * 0.03,
              pdf.internal.pageSize.getHeight() - 8
            );

            props.preDownload(pdf, { currentPage, totalPages });
          }
        })
        .save();
    };
    const download = async () => {
      await pdfRef.value.generatePdf();
      emit('downloaded');
    };

    return { beforeDownload, pdfRef, download };
  },
});
</script>

<style lang="scss">
.cfs-pdf {
  .vue-html2pdf > .layout-container {
    display: none !important;
  }
}
</style>
